import { Pencil, Trash2, ClipboardList } from 'lucide-react';
import React, { useState, useEffect, useMemo } from 'react';
import { visiteAPI, mediciAPI, prestazioniAPI } from '../../services/api';
import SelettorePrestazione from './SelettorePrestazione';
import api from '../../services/api';
import ModuloAttestazione from './ModuloAttestazione';
import { templateAPI } from '../../services/templateApi';
import { imponibileRiga, ivaSu, somma } from '../../utils/denaro';
import { tracciabilitaAPI } from '../../services/tracciabilitaApi';
import ProceduraDettaglio from './ProceduraDettaglio';
import { Package } from 'lucide-react';
import MedicoSelect from '../MedicoSelect';
import FirmaConsenso from './FirmaConsenso';
import SceltaFirma from './SceltaFirma';
import { PannelloRiservato, RichiesteInAttesa } from './AccessoRiservato';
import { useAuthStore } from '../../store/useStore';
import '../../styles/Visite.css';
import '../../styles/FirmaConsenso.css';
import { annunciaProcedureCambiate } from '../../utils/annunci';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import { notificaErrore, messaggioDa } from '../../utils/notifiche';

/* Il nome dell'esecutore imposto, dall'elenco dei medici gia' caricato.
   Se l'elenco non e' ancora arrivato si mostra un ripiego, non un vuoto. */
const nomeMedico = (medici, id) => {
    const m = (medici || []).find((x) => String(x.id) === String(id));
    return m ? `${m.cognome || ''} ${m.nome || ''}`.trim() : 'il tuo profilo medico';
};

const VisiteTab = ({ pazienteId, focusVisitaId, sessoPaziente }) => {
    const utente = useAuthStore((state) => state.user);

    // Procedure riservate aperte in questa sessione, con il dettaglio
    // completo restituito dal backend.
    const [visiteAperte, setVisiteAperte] = useState({});

    /* Le richieste di autorizzazione che riguardano questo paziente, dalle
       due parti: quelle che devo decidere io (ho eseguito la procedura) e
       quelle che ho chiesto io e aspettano risposta. Si caricano una volta
       e si indicizzano per visita: una chiamata invece di una per riga. */
    const [daDecidere, setDaDecidere] = useState({});
    const [mieInAttesa, setMieInAttesa] = useState({});

    /* Apre una procedura riservata per chi e' autorizzato a vederla.
       Il 403 non e' un guasto: e' la risposta a chi deve prima chiedere,
       e il pannello che lo spiega e' gia' a schermo. */
    const apriRiservata = async (id) => {
        try {
            const r = await visiteAPI.apriRiservata(id);
            setVisiteAperte((p) => ({ ...p, [id]: r.data?.data?.visita }));
        } catch (e) {
            if (e?.status !== 403 && e?.response?.status !== 403) {
                console.error('Errore apertura procedura riservata:', e);
            }
        }
    };

    const caricaRichieste = async () => {
        try {
            const r = await visiteAPI.richiesteAccesso();
            const perVisita = (righe, filtro) => {
                const mappa = {};
                (righe || []).filter(filtro).forEach((x) => {
                    mappa[x.visita_id] = [...(mappa[x.visita_id] || []), x];
                });
                return mappa;
            };
            setDaDecidere(perVisita(r.data?.data?.da_decidere, () => true));
            const mie = perVisita(r.data?.data?.mie, (x) => x.stato === 'in_attesa');
            setMieInAttesa(mie);
        } catch (e) {
            // Un medico senza autorizzazioni pendenti non deve vedere errori.
            setDaDecidere({});
            setMieInAttesa({});
        }
    };

    /* Dopo una decisione la riga sparisce dall'elenco senza ricaricare
       tutto: la procedura e' aperta a schermo e un ricaricamento la
       richiuderebbe sotto le mani di chi ha appena deciso. */
    const dopoDecisione = (richiestaId) => {
        setDaDecidere((p) => {
            const nuovo = {};
            Object.entries(p).forEach(([k, v]) => {
                const resto = v.filter((x) => x.id !== richiestaId);
                if (resto.length) nuovo[k] = resto;
            });
            return nuovo;
        });
    };

    /* Un elenco solo dal 22 settembre 2026 (fase 1 del riordino): le due
       cartelle «Prestazioni mediche» / «Medicina estetica» erano una
       questione di menu, non di protezione - a proteggere il dettaglio di
       una procedura estetica e' la riservatezza. visite_tab.cartella resta
       nel database e non decide piu' niente. */
    const [visite, setVisite] = useState([]);
    const [medici, setMedici] = useState([]);
    const [prestazioniCatalogo, setPrestazioniCatalogo] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNewVisitaForm, setShowNewVisitaForm] = useState(false);
    const [expandedVisita, setExpandedVisita] = useState(null);
    /* Visita appena salvata che consuma prodotti a lotto obbligatorio: il
       pannello del numero di lotto compare subito dopo il salvataggio,
       finche' la scatola e' ancora sul tavolo. Chiederlo domani vuol dire
       ricostruirlo a memoria. */
    /* Vero quando la procedura appena salvata ha prodotti a lotto
       obbligatorio: si avvisa il medico di consegnare il tagliando, non
       gli si chiede il numero. */
    const [materialeInCoda, setMaterialeInCoda] = useState(false);
    const [moduliPerVisita, setModuliPerVisita] = useState({});

    /* Chi e' collegato a un medico e' l'esecutore, punto: il server
       ignora qualunque medico_id gli si mandi (services/esecutore.js).
       Qui il campo diventa di sola lettura, perche' chi salva veda a
       nome di chi sta registrando - non e' una protezione, e' un'informazione. */
    const esecutoreImposto = !!(utente && utente.medico_id);

    // Stato del form di nuova procedura (controllato)
    const [formMedicoId, setFormMedicoId] = useState(esecutoreImposto ? utente.medico_id : null);
    const [formPrestazioni, setFormPrestazioni] = useState([]);

    // Consensi richiesti dalle prestazioni selezionate
    const [consensiDisponibili, setConsensiDisponibili] = useState([]);
    const [attestazioniValide, setAttestazioniValide] = useState([]);
    // Attestazione da compilare prima di salvare: { indice, nome }
    const [attestazioneDaFare, setAttestazioneDaFare] = useState(null);
    // null = si deve ancora scegliere, 'pc' = si firma qui
    const [dovesiFirma, setDoveSiFirma] = useState(null);
    const [datiVisitaInAttesa, setDatiVisitaInAttesa] = useState(null);
    const [firmaConsenso, setFirmaConsenso] = useState(true);
    const [consensoDaFirmare, setConsensoDaFirmare] = useState(null);
    /* IL BLOCCO SUL CONSENSO (22 settembre 2026, migration 201): una
       prestazione collegata a un consenso obbligatorio non si registra se
       il paziente non l'ha firmato. Lo decide il database; qui si chiede
       al server, con le stesse funzioni, cosa manca PRIMA di salvare, cosi'
       la firma viene prima della procedura invece che dopo. */
    const [verificaConsensi, setVerificaConsensi] = useState(null);
    // Firma prima del salvataggio: { dati, righe, consensi }
    const [firmaPrima, setFirmaPrima] = useState(null);
    const [dovesiFirmaPrima, setDoveSiFirmaPrima] = useState(null);

    useEffect(() => {
        loadVisite();
        loadMedici();
        loadPrestazioni();
        loadAttestazioni();
        caricaRichieste();
        // eslint-disable-next-line
    }, [pazienteId]);

    // apertura automatica di una procedura richiesta da Timeline/Recap
    useEffect(() => {
        if (focusVisitaId && !loading) {
            setExpandedVisita(focusVisitaId);
            if (!moduliPerVisita[focusVisitaId]) {
                loadModuli(focusVisitaId);
            }
            setTimeout(() => {
                const el = document.getElementById('visita-' + focusVisitaId);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 150);
        }
        // eslint-disable-next-line
    }, [focusVisitaId, loading]);

    // Cosa chiede il blocco sul consenso, per questo paziente e chi esegue
    const prestazioniScelte = [...new Set(formPrestazioni
        .map((p) => p.prestazione_id).filter(Boolean))].join(',');
    useEffect(() => {
        let attivo = true;
        if (!prestazioniScelte) { setVerificaConsensi(null); return undefined; }
        templateAPI.verificaConsensi({
            paziente_id: pazienteId,
            medico_id: formMedicoId || '',
            prestazioni: prestazioniScelte,
        })
            .then((r) => { if (attivo) setVerificaConsensi(r.data.data); })
            .catch((e) => {
                if (attivo) setVerificaConsensi({
                    errore: messaggioDa(e, 'Non riesco a verificare i consensi della prestazione'),
                });
            });
        return () => { attivo = false; };
    }, [prestazioniScelte, formMedicoId, pazienteId]);

    const consensiBloccanti = (verificaConsensi?.prestazioni || []).filter((p) => p.senza_modello);
    /* I modelli da far firmare prima, ciascuno col nome della prestazione
       che lo chiede: FirmaConsenso lo scrive nel documento. */
    const consensiDaFirmarePrima = (verificaConsensi?.modelli || []).map((t) => {
        const chi = (verificaConsensi.prestazioni || []).find((p) => p.mancanti.includes(t.id));
        const riga = chi && formPrestazioni.find((r) => Number(r.prestazione_id) === Number(chi.prestazione_id));
        return { ...t, _prestazione: riga ? riga.nome_snapshot : null };
    });
    const consensiGiaValidi = (verificaConsensi?.prestazioni || []).flatMap((p) => p.validi);

    // Ricerca dei consensi associati alle prestazioni scelte
    useEffect(() => {
        let attivo = true;

        const cerca = async () => {
            const ids = formPrestazioni
                .map((p) => p.prestazione_id)
                .filter(Boolean);

            if (ids.length === 0) {
                if (attivo) setConsensiDisponibili([]);
                return;
            }

            try {
                const trovati = [];
                const visti = new Set();

                for (const riga of formPrestazioni) {
                    if (!riga.prestazione_id) continue;
                    const r = await templateAPI.getPerPrestazione(riga.prestazione_id);
                    /* Solo i FACOLTATIVI si propongono dopo il salvataggio,
                       come prima. Gli obbligatori si firmano prima: vedi
                       verificaConsensi. */
                    (r.data.data.template || []).filter((t) => !t.obbligatorio).forEach((t) => {
                        if (visti.has(t.id)) return;
                        visti.add(t.id);
                        trovati.push({ ...t, _prestazione: riga.nome_snapshot });
                    });
                }

                if (attivo) setConsensiDisponibili(trovati);
            } catch (e) {
                console.error('Errore ricerca consensi:', e);
                if (attivo) setConsensiDisponibili([]);
            }
        };

        cerca();
        return () => { attivo = false; };
    }, [formPrestazioni]);

    const loadVisite = async () => {
        try {
            setLoading(true);
            const response = await visiteAPI.getByPazienteId(pazienteId);
            if (response.data.success) {
                setVisite(response.data.data.visite);
            }
        } catch (error) {
            console.error('Errore caricamento procedure:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadMedici = async () => {
        try {
            const response = await mediciAPI.getAll(true);
            if (response.data.success) {
                setMedici(response.data.data.medici || []);
            }
        } catch (error) {
            console.error('Errore caricamento medici:', error);
        }
    };

    // Attestazioni gia' firmate e ancora valide: un ciclo di sedute
    // si appoggia alla stessa, non se ne rifa' una per ogni seduta.
    const loadAttestazioni = async () => {
        try {
            const r = await api.get(`/attestazioni/valide/${pazienteId}`);
            setAttestazioniValide(r.data.data.attestazioni || []);
        } catch (error) {
            console.error('Errore caricamento attestazioni:', error);
        }
    };

    const loadPrestazioni = async () => {
        try {
            const response = await prestazioniAPI.getAll(true);
            if (response.data.success) {
                setPrestazioniCatalogo(response.data.data.prestazioni || []);
            }
        } catch (error) {
            console.error('Errore caricamento prestazioni:', error);
        }
    };

    const loadModuli = async (visitaId) => {
        try {
            const response = await visiteAPI.getModuli(visitaId);
            if (response.data.success) {
                setModuliPerVisita(prev => ({
                    ...prev,
                    [visitaId]: response.data.data.moduli
                }));
            }
        } catch (error) {
            console.error('Errore caricamento moduli:', error);
        }
    };

    // Prestazioni disponibili nel form: filtro per medico selezionato
    const prestazioniDisponibili = useMemo(() => {
        // Solo servizi: i prodotti si vendono in cassa, qui il medico
        // registra cio' che ha eseguito.
        const soloServizi = prestazioniCatalogo.filter(
            (p) => !p.tipologia || p.tipologia === 'servizio'
        );
        if (!formMedicoId) return soloServizi;
        return soloServizi.filter(
            /* Le voci generiche (nessun medico abilitato) e quelle a cui il
               medico scelto e' abilitato: da una voce un medico solo, dal 22
               settembre 2026 una voce piu' medici (migration 202). */
            (p) => !(p.medici || []).length
                || p.medici.some((m) => String(m.id) === String(formMedicoId))
        );
    }, [prestazioniCatalogo, formMedicoId]);

    /* Le mappe Botox e Filler non ci sono piu' (22 settembre 2026, decisione
       di Alex): Botox si accendeva se il nome della prestazione conteneva
       «botox», e nessuna voce del catalogo lo contiene; Filler compariva
       ma non si salvava mai. Dove si e' intervenuti si segna con «Zone
       trattate», dentro la procedura. */

    /* Totali del form. Si calcolano riga per riga in centesimi interi, nello
       stesso ordine in cui li rifara' la fattura: e' l'unico modo perche' il
       numero letto al paziente prima di salvare sia quello che poi finisce
       stampato. Sommando in virgola mobile lo 0,8% delle righe mostrava un
       centesimo di IVA in meno. */
    const totaliForm = useMemo(() => {
        let imponibile = 0;
        let iva = 0;
        for (const p of formPrestazioni) {
            const riga = imponibileRiga(p.prezzo_unitario, parseFloat(p.quantita) || 0);
            imponibile = somma(imponibile, riga);
            iva = somma(iva, ivaSu(riga, p.iva));
        }
        return { imponibile, iva, totale: somma(imponibile, iva) };
    }, [formPrestazioni]);

    const handleAddPrestazioneRow = () => {
        setFormPrestazioni((prev) => [
            ...prev,
            { prestazione_id: '', nome_snapshot: '', prezzo_unitario: 0, iva: 0, quantita: 1, costo_prodotto_snapshot: 0 },
        ]);
    };

    const handleRemovePrestazioneRow = (index) => {
        setFormPrestazioni((prev) => prev.filter((_, i) => i !== index));
    };

    const handlePrestazioneChange = (index, prestazioneId) => {
        const p = prestazioniCatalogo.find((x) => String(x.id) === String(prestazioneId));
        setFormPrestazioni((prev) =>
            prev.map((row, i) =>
                i === index
                    ? p
                        ? {
                              prestazione_id: p.id,
                              nome_snapshot: p.nome,
                              prezzo_unitario: p.prezzo_indicativo || 0,
                              iva: p.iva || 0,
                              quantita: row.quantita || 1,
                              costo_prodotto_snapshot: p.costo_prodotto || 0,
                              ammette_esenzione: !!p.ammette_esenzione,
                              attestazione_id: null,
                          }
                        : { ...row, prestazione_id: '', nome_snapshot: '' }
                    : row
            )
        );
    };

    const handleAttestazioneChange = (index, valore) => {
        setFormPrestazioni((prev) =>
            prev.map((row, i) => (i === index
                ? { ...row, attestazione_id: valore === '' ? null : valore }
                : row))
        );
    };

    const handleQuantitaChange = (index, quantita) => {
        setFormPrestazioni((prev) =>
            prev.map((row, i) => (i === index ? { ...row, quantita } : row))
        );
    };

    const handlePrezzoChange = (index, prezzo_unitario) => {
        setFormPrestazioni((prev) =>
            prev.map((row, i) => (i === index ? { ...row, prezzo_unitario } : row))
        );
    };

    /* I tre campi liberi del modulo (data, patologia, note) sono
       elementi non controllati, letti al salvataggio: per il confronto
       si copiano qui a ogni cambiamento del modulo, cosi' entrano
       nell'istantanea come tutto il resto. */
    const [campiLiberi, setCampiLiberi] = useState({});
    const leggiCampiLiberi = (e) => {
        const f = new FormData(e.currentTarget);
        setCampiLiberi({
            data_visita: f.get('data_visita') || '',
            patologia: f.get('patologia') || '',
            note_generali: f.get('note_generali') || '',
        });
    };
    const [versioneModulo, setVersioneModulo] = useState(0);
    const sporco = useSporco(
        showNewVisitaForm
            ? { formMedicoId, formPrestazioni, campiLiberi }
            : null,
        versioneModulo
    );
    const chiedi = useUscitaProtetta(sporco);

    const resetForm = () => {
        setFormMedicoId(esecutoreImposto ? utente.medico_id : null);
        setFormPrestazioni([]);
        setConsensiDisponibili([]);
        setFirmaConsenso(true);
        setVerificaConsensi(null);
        setCampiLiberi({});
        setVersioneModulo((v) => v + 1);
    };

    /* Chiudere il modulo con qualcosa dentro chiede conferma: sfondo, X
       e «Annulla» passano tutti da qui. */
    const chiudiModulo = () => {
        if (!chiedi()) return;
        setShowNewVisitaForm(false);
        resetForm();
    };

    /**
     * Prima riga che ha chiesto una nuova attestazione, se c'e'.
     * Il salvataggio si ferma li' finche' non e' stata firmata.
     */
    const prossimaAttestazione = (righe) =>
        righe.findIndex((r) => r.attestazione_id === 'nuova');

    /**
     * Salvataggio vero. Riceve i dati del form e le righe come
     * argomenti perche' viene richiamato anche dopo la firma
     * dell'attestazione, quando l'evento del form non esiste piu'.
     */
    const salvaVisita = async (dati, righe, documentiConsenso = []) => {
        const prestazioniPayload = righe
            .filter((p) => p.nome_snapshot && p.nome_snapshot.trim() !== '')
            .map((p) => ({
                /* L'id della riga, se esisteva gia': e' quello che
                   permette al server di riconciliare invece di
                   cancellare e riscrivere tutto. Una riga nuova non
                   ce l'ha. */
                id: p.id || null,
                prestazione_id: p.prestazione_id || null,
                nome_snapshot: p.nome_snapshot,
                prezzo_unitario: parseFloat(p.prezzo_unitario) || 0,
                iva: parseFloat(p.iva) || 0,
                quantita: parseFloat(p.quantita) || 1,
                costo_prodotto_snapshot: parseFloat(p.costo_prodotto_snapshot) || 0,
                // 'nuova' non e' un id: quell'attestazione non e' stata firmata
                attestazione_id: (p.attestazione_id && p.attestazione_id !== 'nuova')
                    ? p.attestazione_id : null,
            }));

        // 'trattamento' nascosto: auto-popolato dai nomi prestazioni (retrocompatibilita')
        const trattamentoAuto = prestazioniPayload.map((p) => p.nome_snapshot).join(', ');

        try {
            const data = {
                data_visita: dati.data_visita,
                patologia: dati.patologia,
                trattamento: trattamentoAuto || null,
                note_generali: dati.note_generali,
                medico_id: formMedicoId || null,
                prestazioni: prestazioniPayload,
                // Firmati un attimo fa, senza visita: il server li lega a questa
                consensi_documenti: documentiConsenso,
            };

            const res = await visiteAPI.create(pazienteId, data);
            const visitaCreata = res.data?.data?.visita;

            // Consensi da far firmare: catturati prima del reset del form
            const daFirmare = (firmaConsenso && consensiDisponibili.length > 0 && visitaCreata)
                ? {
                    visitaId: visitaCreata.id,
                    medicoId: formMedicoId || null,
                    consensi: consensiDisponibili
                }
                : null;

            setShowNewVisitaForm(false);
            setDatiVisitaInAttesa(null);
            resetForm();
            loadVisite(); annunciaProcedureCambiate();
            /* Se la procedura ha prodotti a lotto obbligatorio si avvisa,
               una volta, che il tagliando va consegnato. Non si blocca
               niente: la coda del magazzino ha comunque la riga. */
            if (visitaCreata) {
                try {
                    const r = await tracciabilitaAPI.daIndicare(visitaCreata.id);
                    setMaterialeInCoda((r.data.data.da_indicare || []).length > 0);
                } catch (e) { /* l'avviso e' una cortesia, non un passaggio */ }
            }
            if (daFirmare) { setDoveSiFirma(null); setConsensoDaFirmare(daFirmare); }
        } catch (error) {
            console.error('Errore creazione procedura:', error);
            notificaErrore(messaggioDa(error, 'Non riesco a creare la procedura'));
        }
    };

    /**
     * Fra il modulo compilato e il salvataggio: se una prestazione chiede un
     * consenso che il paziente non ha firmato, prima si firma. Il server lo
     * rifiuterebbe comunque (migration 201): qui si evita di arrivarci.
     */
    const procedi = (dati, righe) => {
        if (verificaConsensi?.errore) { notificaErrore(verificaConsensi.errore); return; }
        if (consensiBloccanti.length) { notificaErrore(consensiBloccanti[0].messaggio); return; }
        if (consensiDaFirmarePrima.length) {
            setDatiVisitaInAttesa(null);
            setDoveSiFirmaPrima(null);
            setFirmaPrima({ dati, righe, consensi: consensiDaFirmarePrima });
            return;
        }
        salvaVisita(dati, righe);
    };

    /** Firmati tutti: si crea la procedura, e il server lega i documenti. */
    const firmatiPrima = (documenti) => {
        const attesa = firmaPrima;
        setFirmaPrima(null);
        setDoveSiFirmaPrima(null);
        if (!attesa) return;
        const ids = (documenti || []).map((d) => d?.id ?? d?.documento_id).filter(Boolean);
        salvaVisita(attesa.dati, attesa.righe, ids);
    };

    /** L'attestazione e' stata firmata: si aggiorna la riga e si prosegue. */
    const attestazioneCompletata = (attestazioneId) => {
        const indice = attestazioneDaFare.indice;
        const aggiornate = formPrestazioni.map((r, k) =>
            k === indice ? { ...r, attestazione_id: attestazioneId, iva: 0 } : r);
        setFormPrestazioni(aggiornate);
        setAttestazioneDaFare(null);
        loadAttestazioni();

        const altra = prossimaAttestazione(aggiornate);
        if (altra >= 0) {
            setAttestazioneDaFare({ indice: altra, nome: aggiornate[altra].nome_snapshot });
            return;
        }
        if (datiVisitaInAttesa) procedi(datiVisitaInAttesa, aggiornate);
    };

    /**
     * Rinuncia all'attestazione: la prestazione torna imponibile.
     * Meglio toglierla che lasciare il salvataggio a meta'.
     */
    const attestazioneAnnullata = () => {
        const indice = attestazioneDaFare.indice;
        setFormPrestazioni((prev) =>
            prev.map((r, k) => (k === indice ? { ...r, attestazione_id: null } : r)));
        setAttestazioneDaFare(null);
        setDatiVisitaInAttesa(null);
    };

    const handleCreateVisita = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const dati = {};
        formData.forEach((v, k) => { dati[k] = v; });

        // Se serve un'attestazione nuova, prima la si compila e firma
        const daFare = prossimaAttestazione(formPrestazioni);
        if (daFare >= 0) {
            setDatiVisitaInAttesa(dati);
            setAttestazioneDaFare({
                indice: daFare,
                nome: formPrestazioni[daFare].nome_snapshot,
            });
            return;
        }

        procedi(dati, formPrestazioni);
    };

    const handleToggleVisita = (visitaId) => {
        if (expandedVisita === visitaId) {
            setExpandedVisita(null);
        } else {
            setExpandedVisita(visitaId);
            if (!moduliPerVisita[visitaId]) {
                loadModuli(visitaId);
            }
        }
    };

    const handleAddModulo = async (visitaId, tipo) => {
        const modulo = {
            tipo,
            titolo: tipo.charAt(0).toUpperCase() + tipo.slice(1) + ' - ' + new Date().toLocaleDateString('it-IT'),
            contenuto: tipo === 'botox' ? { valori: {}, punti: [] } : {},
            ordine: (moduliPerVisita[visitaId]?.length || 0),
        };

        try {
            await visiteAPI.createModulo(visitaId, modulo);
            loadModuli(visitaId);
        } catch (error) {
            notificaErrore(messaggioDa(error, 'Non riesco a creare il modulo'));
        }
    };

    const handleDeleteVisita = async (id) => {
        if (window.confirm('Eliminare questa procedura? Tutti i moduli e le prestazioni associate verranno eliminati.')) {
            try {
                await visiteAPI.delete(id);
                loadVisite(); annunciaProcedureCambiate();
            } catch (error) {
                notificaErrore(messaggioDa(error, 'Non riesco a eliminare la procedura'));
            }
        }
    };

    const formatEuro = (v) => {
        const n = parseFloat(v);
        if (isNaN(n)) return '€ 0,00';
        return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
    };

    const calcTotaleVisita = (prestazioni) => {
        if (!Array.isArray(prestazioni)) return 0;
        // Stessa aritmetica dei totali del form: due punti che rispondono
        // alla stessa domanda devono dare lo stesso numero.
        return prestazioni.reduce((sum, p) => {
            const riga = imponibileRiga(p.prezzo_unitario, parseFloat(p.quantita) || 0);
            return somma(sum, riga, ivaSu(riga, p.iva));
        }, 0);
    };


    if (loading) {
        return <div className="loading">Caricamento procedure...</div>;
    }

    return (
        <div className="visite-container">
            <div className="visite-header">
                <h2>Visite e procedure mediche</h2>
                <button onClick={() => { setShowNewVisitaForm(true); setVersioneModulo((v) => v + 1); }}
                        className="btn-primary">
                    Nuova procedura
                </button>
            </div>

            {/* IL LOTTO NON SI CHIEDE QUI, E NON E' UNA SEMPLIFICAZIONE
                Prima si apriva un campo appena salvata la procedura,
                "finche' la scatola e' ancora sul tavolo". Ma chi ha la
                scatola in mano ha anche il paziente sul lettino: fermarsi
                a digitare un codice a dodici cifre in quel momento
                significa digitarlo di fretta, e un lotto sbagliato su un
                dispositivo impiantato e' peggio di un lotto indicato
                dopo. Il medico consegna il tagliando alla segreteria, che
                lo registra dalla coda del magazzino leggendolo con calma.
                Il materiale resta in ogni caso segnato come usato: cio'
                che manca e' solo da quale confezione. */}
            {materialeInCoda && (
                <div className="vt-avviso-materiale">
                    <Package size={16} />
                    <div>
                        <b>Materiale tracciato da scaricare.</b>{' '}
                        Consegna il tagliando con il numero di lotto alla segreteria:
                        lo registra lei dalla coda del magazzino. La procedura è già
                        salvata e il prodotto risulta usato.
                        <button className="vt-chiudi-avviso" onClick={() => setMaterialeInCoda(false)}>
                            Ho capito
                        </button>
                    </div>
                </div>
            )}

            {visite.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon"><ClipboardList size={38} strokeWidth={1.2} aria-hidden="true" /></div>
                    <h3>Nessuna procedura</h3>
                    <p>Clicca "Nuova procedura" per iniziare</p>
                </div>
            ) : (
                <div className="visite-list">
                    {visite.map((visita) => {
                        const prestList = visita.prestazioni || [];
                        const totaleVis = calcTotaleVisita(prestList);
                        return (
                            <div key={visita.id} id={`visita-${visita.id}`} className="visita-card">
                                <div className="visita-row">
                                    <div className="visita-info">
                                        <div className="visita-data">
                                            {new Date(visita.data_visita).toLocaleDateString('it-IT')}
                                        </div>
                                        <div className="visita-dettagli">
                                            <span className="visita-patologia">{visita.patologia}</span>
                                            {visita.medico_cognome && (
                                                <>
                                                    <span className="visita-separator">•</span>
                                                    <span className="visita-medico">Dr. {visita.medico_cognome} {visita.medico_nome}</span>
                                                </>
                                            )}
                                            {prestList.length === 0 && visita.trattamento && (
                                                <>
                                                    <span className="visita-separator">•</span>
                                                    <span className="visita-trattamento">{visita.trattamento}</span>
                                                </>
                                            )}
                                        </div>
                                        {prestList.length > 0 && (
                                            <div className="visita-prestazioni-inline">
                                                {prestList.map((p, idx) => (
                                                    <span key={p.id || idx} className="prest-chip">
                                                        {p.nome_snapshot}
                                                        {parseFloat(p.quantita) !== 1 && <span className="prest-qta"> ×{p.quantita}</span>}
                                                    </span>
                                                ))}
                                                <span className="visita-totale">Totale: <strong>{formatEuro(totaleVis)}</strong></span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="visita-actions">
                                        <button onClick={() => handleToggleVisita(visita.id)} className="btn-expand">
                                            {expandedVisita === visita.id ? '▼' : '▶'}
                                        </button>
                                        <button onClick={() => handleDeleteVisita(visita.id)} className="btn-delete-small" title="Elimina procedura"><Trash2 size={15} strokeWidth={1.8} aria-hidden="true" /></button>
                                    </div>
                                </div>

                                {expandedVisita === visita.id && (
                                    <>
                                        {/* Le richieste da decidere si vedono anche
                                            quando la procedura e' aperta: chi ha eseguito
                                            la vede tutta, e la richiesta e' una cosa che
                                            deve poter sbrigare senza uscire da qui. */}
                                        <RichiesteInAttesa
                                            richieste={daDecidere[visita.id]}
                                            onDecisa={dopoDecisione}
                                        />
                                        {visita.riservata_nascosta && !visiteAperte[visita.id] ? (
                                            <PannelloRiservato
                                                visita={visita}
                                                richiestaInAttesa={(mieInAttesa[visita.id] || [])[0]}
                                                onRichiesta={(r) => setMieInAttesa(
                                                    (p) => ({ ...p, [visita.id]: [r] }))}
                                                onAperta={(v) => setVisiteAperte(
                                                    (p) => ({ ...p, [visita.id]: v }))}
                                            />
                                        ) : (
                                            <ProceduraDettaglio
                                                sessoPaziente={sessoPaziente}
                                                visita={visiteAperte[visita.id] || visita}
                                                moduli={moduliPerVisita[visita.id] || []}
                                                onAddModulo={handleAddModulo}
                                                onReload={() => loadModuli(visita.id)}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {showNewVisitaForm && (
                <div className="modal-overlay" onClick={chiudiModulo}>
                    <div className="modal-content modal-procedura" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Nuova procedura</h2>
                            <button className="btn-close" onClick={chiudiModulo}>✕</button>
                        </div>
                        <form onSubmit={handleCreateVisita} onChange={leggiCampiLiberi}>
                            <div className="form-group">
                                <label>Data Procedura *</label>
                                <input type="date" name="data_visita" required />
                            </div>
                            <div className="form-group">
                                <label>Patologia *</label>
                                <input type="text" name="patologia" placeholder="Es: ACNE" required />
                            </div>

                            <div className="form-group">
                                <label>Medico Esecutore</label>
                                {esecutoreImposto ? (
                                    <div className="esecutore-imposto">
                                        Esecutore: {nomeMedico(medici, utente.medico_id)}
                                    </div>
                                ) : (
                                    <MedicoSelect
                                        value={formMedicoId}
                                        onChange={(medicoId) => setFormMedicoId(medicoId)}
                                        medici={medici}
                                    />
                                )}
                            </div>

                            <div className="form-group">
                                <label>Prestazioni eseguite</label>
                                {formPrestazioni.length === 0 ? (
                                    <div className="prestazioni-empty">
                                        Nessuna prestazione aggiunta.
                                    </div>
                                ) : (
                                    <div className="prestazioni-righe">
                                        {formPrestazioni.map((p, idx) => {
                                            const subtot = (parseFloat(p.prezzo_unitario) || 0) * (parseFloat(p.quantita) || 0);
                                            return (
                                                <div key={idx} className="prestazione-riga">
                                                    <SelettorePrestazione
                                                        voci={prestazioniDisponibili}
                                                        valore={p.prestazione_id}
                                                        onChange={(id) => handlePrestazioneChange(idx, id)}
                                                    />
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={p.prezzo_unitario}
                                                        onChange={(e) => handlePrezzoChange(idx, e.target.value)}
                                                        className="prest-prezzo"
                                                        placeholder="Prezzo"
                                                        title="Prezzo unitario"
                                                    />
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={p.quantita}
                                                        onChange={(e) => handleQuantitaChange(idx, e.target.value)}
                                                        className="prest-qta-input"
                                                        placeholder="Q.tà"
                                                        title="Quantità"
                                                    />
                                                    <span className="prest-iva">IVA {parseFloat(p.iva) || 0}%</span>
                                                    <span className="prest-subtot">{formatEuro(subtot)}</span>
                                                    <button
                                                        type="button"
                                                        className="btn-remove-prest"
                                                        onClick={() => handleRemovePrestazioneRow(idx)}
                                                        title="Rimuovi"
                                                    >
                                                        ✕
                                                    </button>
                                                    {p.ammette_esenzione && (
                                                        <div className="prest-esenzione">
                                                            <label>Esenzione IVA</label>
                                                            <select
                                                                value={p.attestazione_id || ''}
                                                                onChange={(e) => handleAttestazioneChange(idx, e.target.value)}
                                                            >
                                                                <option value="">
                                                                    Nessuna — prestazione imponibile
                                                                </option>
                                                                {attestazioniValide.map((a) => (
                                                                    <option key={a.id} value={a.id}>
                                                                        {a.icd10_codice ? `${a.icd10_codice} — ${a.icd10_titolo}` : a.trattamento}
                                                                        {a.valida_al ? ` (fino al ${new Date(a.valida_al).toLocaleDateString('it-IT')})` : ''}
                                                                    </option>
                                                                ))}
                                                                <option value="nuova">
                                                                    + Nuova attestazione…
                                                                </option>
                                                            </select>
                                                            {p.attestazione_id === 'nuova' && (
                                                                <span className="prest-esenzione-nota">
                                                                    Alla chiusura della procedura andrà compilata e firmata,
                                                                    altrimenti la prestazione resta imponibile.
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                <button type="button" className="btn-add-prest" onClick={handleAddPrestazioneRow}>
                                    Aggiungi prestazione
                                </button>

                                {attestazioneDaFare && (
                                    <ModuloAttestazione
                                        pazienteId={pazienteId}
                                        trattamentoIniziale={attestazioneDaFare.nome}
                                        onCompletata={attestazioneCompletata}
                                        onAnnulla={attestazioneAnnullata}
                                    />
                                )}

                                {formPrestazioni.length > 0 && (
                                    <div className="prestazioni-totali">
                                        <span>Imponibile: <strong>{formatEuro(totaliForm.imponibile)}</strong></span>
                                        <span>IVA: <strong>{formatEuro(totaliForm.iva)}</strong></span>
                                        <span className="totale-finale">Totale: <strong>{formatEuro(totaliForm.totale)}</strong></span>
                                    </div>
                                )}
                            </div>

                            {/* ---------- Consenso informato ----------
                                Prima gli obbligatori, che fermano; poi i
                                facoltativi, che si propongono dopo. */}
                            {verificaConsensi?.errore && (
                                <div className="form-group consenso-box consenso-box--blocco">
                                    <strong>{verificaConsensi.errore}</strong>
                                </div>
                            )}
                            {consensiBloccanti.map((p) => (
                                <div key={`blocco-${p.prestazione_id}`}
                                     className="form-group consenso-box consenso-box--blocco">
                                    <strong>{p.messaggio}</strong>
                                </div>
                            ))}
                            {!consensiBloccanti.length && consensiDaFirmarePrima.length > 0 && (
                                <div className="form-group consenso-box">
                                    <strong className="consenso-titolo">
                                        Consenso obbligatorio: il paziente firma prima che la procedura venga creata
                                    </strong>
                                    <ul className="consenso-elenco">
                                        {consensiDaFirmarePrima.map((t) => (
                                            <li key={t.id}>
                                                {t.titolo}
                                                {t._prestazione ? <em> — {t._prestazione}</em> : null}
                                            </li>
                                        ))}
                                    </ul>
                                    <small className="consenso-nota">
                                        Senza la firma la prestazione non si registra.
                                    </small>
                                </div>
                            )}
                            {consensiGiaValidi.length > 0 && (
                                <div className="form-group consenso-box consenso-box--valido">
                                    {consensiGiaValidi.map((c) => (
                                        <small key={c.template_id} className="consenso-nota">
                                            «{c.titolo}» firmato dal paziente il{' '}
                                            {new Date(c.firmato_il).toLocaleDateString('it-IT')}: vale anche per questa procedura.
                                        </small>
                                    ))}
                                </div>
                            )}
                            {consensiDisponibili.length > 0 && (
                                <div className="form-group consenso-box">
                                    <label className="consenso-check">
                                        <input
                                            type="checkbox"
                                            checked={firmaConsenso}
                                            onChange={(e) => setFirmaConsenso(e.target.checked)}
                                        />
                                        <span>
                                            <strong>Compilazione e firma consenso</strong>
                                            <small>
                                                Al salvataggio {consensiDisponibili.length === 1
                                                    ? 'verrà proposto il consenso'
                                                    : `verranno proposti ${consensiDisponibili.length} consensi`} da far firmare al paziente.
                                            </small>
                                        </span>
                                    </label>
                                    <ul className="consenso-elenco">
                                        {consensiDisponibili.map((t) => (
                                            <li key={t.id}>
                                                {t.titolo}
                                                {t._prestazione ? <em> — {t._prestazione}</em> : null}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="form-group">
                                <label>Note Generali</label>
                                <textarea name="note_generali" rows="3" placeholder="Note opzionali..."></textarea>
                            </div>
                            <div className="modal-footer">
                                <button type="button" onClick={chiudiModulo} className="btn-secondary">
                                    Annulla
                                </button>
                                <button type="submit" className="btn-primary"
                                        disabled={consensiBloccanti.length > 0}>
                                    {consensiDaFirmarePrima.length && !consensiBloccanti.length
                                        ? 'Fai firmare e crea la procedura'
                                        : 'Crea Procedura'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Consensi OBBLIGATORI: si firmano prima di creare la procedura.
                Stessa scelta PC/tablet dei facoltativi, ma senza visita: il
                server lega i documenti quando la crea. Chiudere qui vuol dire
                non creare la procedura. */}
            {firmaPrima && dovesiFirmaPrima === null && (
                <SceltaFirma
                    pazienteId={pazienteId}
                    visitaId={null}
                    medicoId={formMedicoId || null}
                    consensi={firmaPrima.consensi}
                    onSuPc={() => setDoveSiFirmaPrima('pc')}
                    onCompletato={firmatiPrima}
                    onAnnulla={() => { setFirmaPrima(null); setDoveSiFirmaPrima(null); }}
                />
            )}
            {firmaPrima && dovesiFirmaPrima === 'pc' && (
                <FirmaConsenso
                    pazienteId={pazienteId}
                    visitaId={null}
                    medicoId={formMedicoId || null}
                    consensi={firmaPrima.consensi}
                    obbligatori
                    onClose={() => { setFirmaPrima(null); setDoveSiFirmaPrima(null); }}
                    onComplete={firmatiPrima}
                />
            )}

            {/* Prima si decide dove firma il paziente, poi si apre
                la schermata giusta. */}
            {consensoDaFirmare && dovesiFirma === null && (
                <SceltaFirma
                    pazienteId={pazienteId}
                    visitaId={consensoDaFirmare.visitaId}
                    medicoId={consensoDaFirmare.medicoId}
                    consensi={consensoDaFirmare.consensi}
                    onSuPc={() => setDoveSiFirma('pc')}
                    onCompletato={() => {
                        setConsensoDaFirmare(null);
                        setDoveSiFirma(null);
                        loadVisite(); annunciaProcedureCambiate();
                    }}
                    onAnnulla={() => {
                        setConsensoDaFirmare(null);
                        setDoveSiFirma(null);
                    }}
                />
            )}

            {consensoDaFirmare && dovesiFirma === 'pc' && (
                <FirmaConsenso
                    pazienteId={pazienteId}
                    visitaId={consensoDaFirmare.visitaId}
                    medicoId={consensoDaFirmare.medicoId}
                    consensi={consensoDaFirmare.consensi}
                    onClose={() => { setConsensoDaFirmare(null); setDoveSiFirma(null); }}
                    onComplete={() => loadVisite()}
                />
            )}
        </div>
    );
};

export default VisiteTab;
